import React, { useState, useRef, forwardRef, useImperativeHandle, useEffect, useCallback } from 'react';
import { useNotification } from '../../../hooks/useNotification';
import { useErrorInputs } from '../../../hooks/useErrorInputs';
import { useApiRequest } from '../../../hooks/useApiRequest';
import { useDataAuth } from '../../../hooks/useDataAuth';
import { useTranslation } from "react-i18next";
import TableContainer from '@mui/material/TableContainer';
import DeleteIcon from '@mui/icons-material/Delete';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import TableBody from '@mui/material/TableBody';
import CheckIcon from '@mui/icons-material/Check';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TextField from '@mui/material/TextField';
import EditIcon from '@mui/icons-material/Edit';
import TableRow from '@mui/material/TableRow';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Table from '@mui/material/Table';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';

import { GenericAutocomplete } from '../../others/GenericAutocomplete';
import { fetchServiceJson, fetchProducts } from '../../../services/common.services';
import { formatNumber } from '../../../utils/invoice';

const required = () => <Typography variant="subtitle2" component="span" color="error" sx={{ mr: 0.5 }}>* </Typography>
export const ProductsForm = forwardRef(({
    dto,
    refreshTotals,
    handleInputChange,
    handleDirectInputChange,
    isView,
    changeValueAccess = false
}, ref) => {
    const GetInitialStateProduct = () => ({
        id: new Date().getTime().toString(),
        id_product_service: '',
        code: '',
        name: '',
        quantity: '',
        unitMeasurementId: '',
        price: '',
        discount: 0,
        tax: '',
        total: 0,
        isCreated: true,
        isDisabled: false
    });

    const [sendNotification] = useNotification();
    const [{ userSession }] = useDataAuth();
    const apiRequest = useApiRequest();
    const { t } = useTranslation();

    const products = dto.products;

    const [productos, setProductos] = useState([GetInitialStateProduct()]);
    const [errorInputs, handleErrorInput] = useErrorInputs({});
    const productRef = useRef(null);
    const quantityRef = useRef(null);
    const [pendingIndex, setPendingIndex] = useState(null);
    const [allProducts, setAllProducts] = useState([]);
    const [idProduct, setIdProduct] = useState('');
    const [quantity, setQuantity] = useState('');
    const [taxes, setTaxes] = useState([]);

    useEffect(() => {
        if (products.length > 0) {
            setProductos(products);
            calcularTotalesGenerales();
        }
    }, [products, refreshTotals])

    const requiredField = ['code', 'name', 'quantity', 'price', 'tax'];

    const validateField = (index, name, value) => {
        if (['isCreated'].includes(name)) {
            handleErrorInput(`${index}-${name}`, { isError: true, messageError: 'isCreated' });
            return true;
        }

        if (typeof value !== 'string') {
            value = String(value || '');
        }

        if (value.trim() === '') {
            handleErrorInput(`${index}-${name}`, { isError: true, messageError: `${name} es obligatorio.` });
            return false;
        } else {
            handleErrorInput(`${index}-${name}`, { isError: false, messageError: '' });
            return true;
        }
    };

    const validForm = () => {
        const creating = productos.some(product => product.isCreated);
        if (productos.length > 0 && (productos[0].name !== '' && productos[0].code !== '') && !creating) {
            return true;
        }
        return false;
    };

    useImperativeHandle(ref, () => ({
        getData: () => productos,
        validForm,
        errorInputs,
    }));

    const getPercent = (searchNum = '') => {
        const parts = searchNum.split("(");
        if (parts.length > 1) {
            const num = parts[1].replace("%)", "").trim();
            return parseFloat(num) || 0;
        }
        return 0;
    };

    const calcularTotalesGenerales = () => {
        return productos.reduce(
            (totales, product) => {
                const quantity = parseFloat(product.quantity) || 0;
                const price = parseFloat(product.price) || 0;
                const discountPercent = parseFloat(product.discount) || 0;
                const porcentajeImp = getPercent(product.tax);
                const subtotal = quantity * price;
                const discount = (discountPercent / 100) * subtotal;
                const totalCondiscount = subtotal - discount;
                const totalImp = totalCondiscount * (porcentajeImp / 100);
                const total = totalCondiscount + totalImp;
                product.total = total;
                totales.price += price;
                totales.tax += totalImp;
                totales.gross_total_value += subtotal;
                totales.quantity += quantity;
                totales.discount += discount;
                totales.totalGeneral += total;
                handleDirectInputChange('discount', totales.discount.toString());
                handleDirectInputChange('tax_amount', totales.tax.toString());
                handleDirectInputChange('gross_total_value', totales.gross_total_value.toString());
                handleDirectInputChange('payable_amount', totales.totalGeneral.toString());
                handleDirectInputChange('price', totales.totalGeneral.toString());
                return totales;
            },
            {
                tax: 0,
                price: 0,
                gross_total_value: 0,
                quantity: 0,
                discount: 0,
                totalGeneral: 0
            }
        );
    };

    const handleFieldChange = (index, field, value) => {
        if (field === 'discount') {
            value = value === '' ? 0 : value.toString().replace(/^0+(?=\d)/, '');
        }
        const updatedProductos = productos.map((producto, i) => {
            if (i === index) {
                const updatedProduct = { ...producto, [field]: value };
                return updatedProduct;
            }
            return producto;
        });
        setProductos(updatedProductos);
    };

    const handleSearch = useCallback((value) => {
        setTimeout(async () => {
            let variables = { "code_sku": value };
            const data = await apiRequest({
                url: '',
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                data: JSON.stringify({
                    "process": "product_service_select",
                    "action": "SELECT_BY_SERVICE_CODE",
                    "variables": JSON.stringify(variables)
                })
            });
            if (data && data.length > 0) {
                updateProducts(value, data[0], true);
            } else {
                const index = productos.findIndex(product => product.isCreated);
                if (index !== -1) {
                    setProductos(productos.map((p, i) => i === index ? { ...GetInitialStateProduct() } : p));
                    sendNotification({ msg: t('validation.codeExistingProduct'), variant: 'info' });
                }
            }
        }, 2000);
    }, [apiRequest]);

    const updateProducts = (value, data, creating = false) => {
        setProductos(prevProductos => {
            let currentIndex = prevProductos.findIndex(
                i => i.isCreated
            );
            let updatedProducts = [...prevProductos];
            const base = {
                ...data,
                id: crypto.randomUUID().toString(),
                name: data.name.split('(')[0].trim(),
                code: value,
                quantity,
                isCreated: creating,
                isDisabled: creating
            };
            if (currentIndex !== -1) {
                updatedProducts[currentIndex] = base;
            } else {
                updatedProducts.push(base);
                currentIndex = updatedProducts.length - 1;
            }
            setPendingIndex(currentIndex);
            return updatedProducts;
        });
    };

    const [totals, setTotals] = useState({
        discount: 0,
        tax: 0,
        totalGeneral: 0,
    })

    const handleSaveProduct = (index) => {
        let product = productos[index];
        const isValid = requiredField.every((field) => validateField(index, field, product[field]));
        if (isValid) {
            const quantity = parseFloat(product.quantity) || 0;
            const price = parseFloat(product.price) || 0;
            const discount = parseFloat(product.discount) || 0;
            const porcentajeImp = getPercent(product.tax);
            const subtotal = quantity * price;
            const totalCondiscount = subtotal - discount;
            const totalImp = totalCondiscount * (porcentajeImp / 100);
            const total = totalCondiscount + totalImp;
            product.total = total;
            product.isCreated = false;
            product.isDisabled = false;
            setTotals(calcularTotalesGenerales());
            handleDirectInputChange('products', productos);
            sendNotification({ msg: t('messages.saveSuccess'), variant: 'success' });
        } else {
            sendNotification({ msg: t('validation.requiredFieldsMissing'), variant: 'error' });
        }
    };

    const handleEditProduct = (index) => {
        let creatingProduct = productos.some(product => product.isCreated);
        if (creatingProduct) return
        const updatedProductos = productos.map((prod, i) =>
            i === index ? { ...prod, isCreated: true, isDisabled: true } : prod
        );
        setProductos(updatedProductos);
    };

    const handleDeleteProduct = (index) => {
        let updatedProductos = productos.filter((_, i) => i !== index);
        if (updatedProductos.length === 0) {
            updatedProductos.push({ ...GetInitialStateProduct() });
        }
        setProductos(updatedProductos);
        handleDirectInputChange('products', updatedProductos);
        setTimeout(() => {
            const totales = calcularTotalesGenerales();
            setTotals(totales);
        }, 800);
    };

    const handleQuantityConfirm = () => {
        if (quantity.trim() === "" || idProduct.trim() === "") return;
        const foundProduct = allProducts.find(product => `${product.id_product_service}` === idProduct);
        if (!foundProduct) return;

        const { code: _omit, ...productWithoutCode } = foundProduct;
        updateProducts(foundProduct.code_sku, productWithoutCode);

        setIdProduct("");
        setQuantity("");
        productRef.current?.focus();

    };

    const handleQuantityKeyDown = (e) => {
        if (e.key === "Enter" || e.key === "Tab") {
            e.preventDefault();
            handleQuantityConfirm();
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await fetchServiceJson("MENU-ORDEN_DE_COMPRA", userSession.token);
                const responseData = await fetchProducts(userSession.token)
                if (!response?.data || !responseData?.data) return;
                setAllProducts(responseData?.data.map(product => ({ ...product, id_product_service: product.code, name: `${product.name} (${product.code_sku})` })));
                const parsedData = response.data.reduce((acc, item) => {
                    if (item.field === "tax") {
                        acc.tax = JSON.parse(item.value);
                    }
                    return acc;
                }, {});

                if (parsedData.tax) setTaxes(parsedData.tax);

            } catch (error) {
                console.error("Error fetching data:", error);
            }
        };
        fetchData();
    }, [userSession.token]);

    useEffect(() => {
        if (pendingIndex !== null) {
            handleSaveProduct(pendingIndex);
            setPendingIndex(null);
        }
    }, [pendingIndex, productos]);

    useEffect(() => {
        if (Array.isArray(products) && products.length > 0) {
            setTotals(calcularTotalesGenerales());
        }
    }, [products, productos]);

    return (
        <>
            <Box sx={{ width: "100%", mx: "auto", mt: 3, mb: 2 }}>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <GenericAutocomplete
                            inputRef={productRef}
                            options={allProducts}
                            value={idProduct}
                            label={t('selectPlaceholder')}
                            onChange={({ target: { value } }) => setIdProduct(`${value}`)}
                            disableCloseOnSelect={false}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 2 }}>
                        <TextField fullWidth size="small"
                            placeholder={t("crm.products.quantity")}
                            value={quantity}
                            onChange={(e) => setQuantity(e.target.value)}
                            onKeyDown={handleQuantityKeyDown}
                            // onBlur={handleQuantityBlur}
                            inputRef={quantityRef}
                        />
                    </Grid>

                </Grid>
            </Box>

            <TableContainer
                component={Paper}
                sx={{ mb: 3, bgcolor: '#f0f1f5', overflowX: 'auto' }}
            >
                <Box>
                    <Typography variant="inherit" align="left">
                        {t("messages.asteriskRequiredNote")}
                    </Typography>
                </Box>
                <Table sx={{ minWidth: 1000 }} aria-label="productos table">
                    <TableHead>
                        <TableRow key={'rowHead'} sx={{ bgcolor: '#e0e0e0' }}>
                            <TableCell key={'codeLabel'}>
                                <Typography variant="subtitle2" component="span"> {required()}{t('crm.products.code')}</Typography>
                            </TableCell>
                            <TableCell key={'descriptionLabel'}>
                                <Typography variant="subtitle2" component="span"> {required()}{t('crm.products.name')}</Typography>
                            </TableCell>
                            <TableCell key={'cantidadLabel'}>
                                <Typography variant="subtitle2" component="span"> {required()}{t('crm.products.quantity')}</Typography>
                            </TableCell>
                            <TableCell key={'valueUnitLabel'}>
                                <Typography variant="subtitle2" component="span"> {required()}{t('crm.products.unitValue')}</Typography>
                            </TableCell>
                            <TableCell key={'discountLabel'}>
                                <Typography variant="subtitle2" component="span"> {`${t('crm.products.discount')} (%)`}</Typography>
                            </TableCell>
                            <TableCell key={'taxLabel'}>
                                <Typography variant="subtitle2" component="span"> {required()}{t('crm.products.taxes')}</Typography>
                            </TableCell>
                            <TableCell key={'totalLabel'} padding='normal'> {t('crm.products.total')}</TableCell>
                            <TableCell key={'actionLabel'} padding='normal' sx={{ minWidth: 80 }} />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {productos.map((product, index) => (
                            <TableRow key={index} sx={{ backgroundColor: product.isCreated && 'white' }}>
                                <TableCell key={'code'}>
                                    <TextField fullWidth required size="small" disabled={!product.isCreated || product.isDisabled} sx={{ width: '115%' }}
                                        value={product.code}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            handleFieldChange(index, 'code', value);
                                        }}
                                        onBlur={(e) => {
                                            const value = e.target.value;
                                            if (value.length >= 3) {
                                                handleSearch(value);
                                            }
                                        }}
                                        error={!!errorInputs[`${index}-code`]?.isError}
                                    />
                                </TableCell>
                                <TableCell key={'name'}>
                                    <TextField fullWidth required disabled={!changeValueAccess || !product.isCreated} sx={{ width: '115%' }} size="small"
                                        value={product.name}
                                        onChange={(e) => handleFieldChange(index, 'name', e.target.value)}
                                        error={!!errorInputs[`${index}-name`]?.isError}
                                    />
                                </TableCell>
                                <TableCell key={'quantity'}>
                                    <TextField fullWidth required sx={{ width: '115%' }} disabled={!product.isCreated} size="small" type='number' className='input-no-spinner'
                                        value={product.quantity}
                                        onChange={(e) => handleFieldChange(index, 'quantity', e.target.value)}
                                        error={!!errorInputs[`${index}-quantity`]?.isError}
                                    />
                                </TableCell>
                                <TableCell key={'price'}>
                                    <TextField fullWidth required disabled={!changeValueAccess || !product.isCreated} size="small" sx={{ width: '115%' }} type='number' className='input-no-spinner'
                                        value={product.price}
                                        onChange={(e) => handleFieldChange(index, 'price', e.target.value)}
                                        error={!!errorInputs[`${index}-price`]?.isError}
                                    />
                                </TableCell>
                                <TableCell key={'discount'}>
                                    <TextField fullWidth required disabled={!changeValueAccess || !product.isCreated} size="small" sx={{ width: '115%' }} type='number' className='input-no-spinner'
                                        value={product.discount}
                                        onChange={(e) => handleFieldChange(index, 'discount', e.target.value)}
                                        error={!!errorInputs[`${index}-discount`]?.isError}
                                    />
                                </TableCell>
                                <TableCell key={'tax'}>
                                    <FormControl fullWidth required size="small" disabled
                                        error={!!errorInputs.tax?.isError}
                                    >
                                        <Select size="small" sx={{ width: 120 }} MenuProps={{ PaperProps: { style: { maxHeight: 200, overflowY: "auto" } } }}
                                            value={product.tax}
                                            onChange={(e) => handleFieldChange(index, 'tax', e.target.value)}
                                        >
                                            {taxes.map((tax, i) => <MenuItem key={i} value={tax.code}>{tax.name}%</MenuItem>)}
                                        </Select>
                                    </FormControl>
                                </TableCell>

                                <TableCell>
                                    {formatNumber(product.total)}
                                </TableCell>
                                <TableCell>
                                    <IconButton disabled={isView}
                                        title={product.isCreated ? 'Guardar' : 'Editar'}
                                        color={product.isCreated ? 'success' : 'primary'}
                                        onClick={() => (product.isCreated ? handleSaveProduct(index) : handleEditProduct(index))}
                                    >
                                        {product.isCreated ? <CheckIcon /> : <EditIcon />}
                                    </IconButton>
                                    <IconButton color="secondary" disabled={isView}
                                        onClick={() => handleDeleteProduct(index)}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer >

            <Box sx={{ mb: 3 }}>
                <Grid container spacing={9} alignItems="flex-start">
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="h6" gutterBottom>{t('commonFields.notes')}</Typography>
                        <TextField fullWidth multiline rows={4} name="observations" size="small"
                            // label={t('commonFields.notes')}
                            value={dto.observations}
                            onChange={handleInputChange}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, md: 6 }} sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
                        <Typography variant="subtitle1">Totales</Typography>
                        <Box
                            sx={{ textAlign: "right", p: 2, borderRadius: 2, bgcolor: "grey.100", boxShadow: 3, minWidth: "60%", marginTop: '10px' }}
                        >
                            <Typography variant="body2" fontWeight="bold">{t('crm.products.discountTotal')}: {formatNumber(totals.discount)}</Typography>
                            <Typography variant="body2" fontWeight="bold">{t('crm.products.taxTotal')}: {formatNumber(totals.tax)}</Typography>
                            <Typography variant="body2" fontWeight="bold">{t('commonFields.retentions')}: {formatNumber(dto.total_retentions)}</Typography>
                            <Typography variant="body2" fontWeight="bold">{t('commonFields.grossTotalValue')}: {formatNumber(totals.gross_total_value)}</Typography>
                            <Typography variant="body2" fontWeight="bold">{t('crm.products.generalTotal')}: {formatNumber(totals.totalGeneral)}</Typography>
                        </Box>
                    </Grid>
                </Grid>
            </Box>
        </>
    );
});